import { z } from 'zod';

export const NotificationPrivacy = z.enum([
  'count',
  'sender',
  'sender_subject',
]);
export type NotificationPrivacy = z.infer<typeof NotificationPrivacy>;

export const NotificationAccountPreference = z.object({
  accountId: z.number().int().positive(),
  enabled: z.boolean(),
});
export type NotificationAccountPreference = z.infer<
  typeof NotificationAccountPreference
>;

export const NotificationPreferences = z.object({
  mailEnabled: z.boolean(),
  plannerEnabled: z.boolean(),
  calendarEnabled: z.boolean(),
  badgeEnabled: z.boolean(),
  privacy: NotificationPrivacy,
  quietHoursEnabled: z.boolean(),
  quietStartMinutes: z.number().int().min(0).max(1439),
  quietEndMinutes: z.number().int().min(0).max(1439),
  timeZone: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .refine(isTimeZone, 'expected an IANA time zone such as America/Toronto'),
  accounts: z.array(NotificationAccountPreference).max(100),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferences>;

export function defaultNotificationPreferences(
  timeZone = 'UTC',
): NotificationPreferences {
  return {
    mailEnabled: true,
    plannerEnabled: true,
    calendarEnabled: true,
    badgeEnabled: true,
    privacy: 'sender',
    quietHoursEnabled: false,
    quietStartMinutes: 22 * 60,
    quietEndMinutes: 7 * 60,
    timeZone,
    accounts: [],
  };
}

export const NotificationDevice = z.object({
  id: z.number().int().positive(),
  endpointHash: z.string().regex(/^[0-9a-f]{64}$/),
  name: z.string(),
  createdAt: z.number().int(),
  lastSeenAt: z.number().int(),
  lastSuccessAt: z.number().int().nullable(),
  lastFailureAt: z.number().int().nullable(),
  lastError: z.string().nullable(),
});
export type NotificationDevice = z.infer<typeof NotificationDevice>;

export const NotificationState = z.object({
  publicKey: z.string().min(1),
  preferences: NotificationPreferences,
  devices: z.array(NotificationDevice),
});
export type NotificationState = z.infer<typeof NotificationState>;

const PushKey = z
  .string()
  .min(16)
  .max(512)
  .regex(/^[A-Za-z0-9_-]+$/, 'expected base64url key material');

const PushEndpoint = z
  .url()
  .max(4096)
  .refine((value) => /^https:\/\//i.test(value), {
    message: 'push endpoint must use HTTPS',
  });

export const PushSubscriptionUpsert = z.object({
  endpoint: PushEndpoint,
  expirationTime: z.number().int().positive().nullable(),
  keys: z.object({
    p256dh: PushKey,
    auth: PushKey,
  }),
  deviceName: z.string().trim().min(1).max(80),
  replaceEndpoint: PushEndpoint.optional(),
});
export type PushSubscriptionUpsert = z.infer<typeof PushSubscriptionUpsert>;

export const PushSubscriptionResponse = z.object({
  device: NotificationDevice,
});
export type PushSubscriptionResponse = z.infer<
  typeof PushSubscriptionResponse
>;

export const NotificationTestRequest = z.object({
  subscriptionId: z.number().int().positive().optional(),
});
export type NotificationTestRequest = z.infer<typeof NotificationTestRequest>;

export const NotificationTestResponse = z.object({
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type NotificationTestResponse = z.infer<
  typeof NotificationTestResponse
>;

function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}
